import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Menu, X, ChevronDown, CandlestickChart, Landmark, LineChart, GraduationCap,
  BookOpen, FileText, Bot, Sparkles, BadgeCheck, Gamepad2, Rocket, Zap, Crosshair, Waves
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const NAV_GROUPS = [
  {
    name: 'Markets',
    items: [
      { name: 'Live Chart & Data', path: '/live-chart', icon: CandlestickChart, desc: 'TradingView chart, heatmap, calendar' },
      { name: 'Whale Watch', path: '/whale-watch', icon: Waves, desc: 'Live on-chain whale transactions' },
      { name: 'Demo Trading', path: '/trading', icon: Gamepad2, desc: '$100k virtual trading arena' },
      { name: 'Institutional Analysis', path: '/analysis', icon: Landmark, desc: 'Probability-weighted forecasts', pro: true },
    ],
  },
  {
    name: 'Learn',
    items: [
      { name: 'Start Here', path: '/start-here', icon: Rocket, desc: 'Guided path: beginner → advanced' },
      { name: 'Chart Reader', path: '/chart-reader', icon: Crosshair, desc: 'Interactive chart-reading game' },
      { name: 'Crypto Research', path: '/research', icon: GraduationCap, desc: '14 blockchains, quizzes & badges' },
      { name: 'Education Hub', path: '/education', icon: BookOpen, desc: 'Courses & trading guides' },
      { name: 'Pattern Library', path: '/patterns', icon: LineChart, desc: '43 professional chart patterns' },
      { name: 'Macro Brief', path: '/macro-brief', icon: FileText, desc: 'Weekly institutional research' },
    ],
  },
  {
    name: 'Intelligence',
    items: [
      { name: 'Signal Engine', path: '/signal-engine', icon: Zap, desc: 'Live confluence scores, glass-box logic', pro: true },
      { name: 'AI Research Agent', path: '/ai-advisor', icon: Bot, desc: 'Chat with a live-data AI analyst', pro: true },
      { name: 'Intelligence Feed', path: '/intelligence', icon: Sparkles, desc: 'AI market intelligence', pro: true },
      { name: 'Track Record', path: '/track-record', icon: BadgeCheck, desc: 'Every AI call, publicly verified' },
    ],
  },
];

const DIRECT_LINKS = [
  { name: 'News', path: '/news' },
  { name: 'Forum', path: '/forum' },
  { name: 'About', path: '/about' },
];

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { user, isAuthenticated, login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isGroupActive = (group) => group.items.some(i => location.pathname.startsWith(i.path));

  return (
    <nav className="fixed top-10 left-0 right-0 z-50 glass border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group flex-shrink-0" data-testid="logo-link">
            <div className="w-9 h-9 bg-gradient-to-r from-[#C5A059] to-[#E5C578] rounded-sm flex items-center justify-center font-bold text-black text-lg">
              DG
            </div>
            <span className="font-manrope font-bold text-lg text-white group-hover:text-[#C5A059] transition-colors hidden sm:inline">
              DigitalGoldGang
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-0.5">
            <Link to="/" data-testid="nav-home"
              className={`px-3 py-2 text-sm font-medium transition-colors rounded-sm hover:bg-white/5 ${location.pathname === '/' ? 'text-[#C5A059]' : 'text-gray-300 hover:text-[#C5A059]'}`}>
              Home
            </Link>

            {NAV_GROUPS.map(group => (
              <DropdownMenu key={group.name}>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid={`nav-group-${group.name.toLowerCase()}`}
                    className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-sm hover:bg-white/5 outline-none ${isGroupActive(group) ? 'text-[#C5A059]' : 'text-gray-300 hover:text-[#C5A059]'}`}>
                    {group.name} <ChevronDown size={13} className="opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-[#111111] border-white/10 text-white w-72 p-2">
                  {group.items.map(item => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem key={item.path} asChild
                        className="cursor-pointer rounded-lg focus:bg-white/5 data-[highlighted]:bg-white/5 p-0">
                        <Link to={item.path} data-testid={`nav-item-${item.path.replace('/', '')}`}
                          className="flex items-start gap-3 px-3 py-2.5 w-full">
                          <div className="w-8 h-8 rounded-lg bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Icon size={15} className="text-[#C5A059]" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-white">{item.name}</span>
                              {item.pro && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#C5A059]/20 text-[#C5A059] border border-[#C5A059]/40">PRO</span>
                              )}
                            </div>
                            <p className="text-[11px] text-gray-500 mt-0.5">{item.desc}</p>
                          </div>
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ))}

            {DIRECT_LINKS.map(link => (
              <Link key={link.path} to={link.path} data-testid={`nav-${link.name.toLowerCase()}`}
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-sm hover:bg-white/5 ${location.pathname.startsWith(link.path) ? 'text-[#C5A059]' : 'text-gray-300 hover:text-[#C5A059]'}`}>
                {link.name}
              </Link>
            ))}
          </div>

          {/* Auth Section */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            {isAuthenticated && user ? (
              <>
                {user.role === 'admin' && (
                  <Button
                    onClick={() => navigate('/admin')}
                    variant="ghost"
                    className="text-[#C5A059] hover:text-[#D4AF37] hover:bg-white/5 px-3"
                    data-testid="admin-button"
                  >
                    Admin
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center space-x-2 hover:opacity-80 transition-opacity" data-testid="user-menu-button">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback className="bg-[#C5A059] text-black font-semibold">
                          {user.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#111111] border-white/10 text-white">
                    <DropdownMenuItem
                      onClick={() => navigate(`/profile/${user.user_id}`)}
                      className="hover:bg-white/5 cursor-pointer"
                      data-testid="profile-menu-item"
                    >
                      My Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={logout}
                      className="text-red-400 hover:text-red-300 hover:bg-white/5 cursor-pointer"
                      data-testid="logout-button"
                    >
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button
                onClick={login}
                className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm px-5 py-2 font-semibold uppercase text-xs shadow-[0_0_15px_rgba(197,160,89,0.3)] hover:shadow-[0_0_25px_rgba(197,160,89,0.5)] transition-all"
                data-testid="login-button"
              >
                Join Community
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2 hover:bg-white/5 rounded-sm transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden glass border-t border-white/10 max-h-[75vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-4">
            <Link to="/" onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 text-sm font-medium text-gray-300 hover:text-[#C5A059] hover:bg-white/5 rounded-sm">
              Home
            </Link>
            {NAV_GROUPS.map(group => (
              <div key={group.name}>
                <p className="px-3 text-[11px] font-bold text-[#C5A059] uppercase tracking-wider mb-1">{group.name}</p>
                {group.items.map(item => (
                  <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2 text-sm font-medium text-gray-300 hover:text-[#C5A059] hover:bg-white/5 rounded-sm">
                    {item.name} {item.pro && <span className="text-[9px] font-bold text-[#C5A059]">PRO</span>}
                  </Link>
                ))}
              </div>
            ))}
            <div>
              <p className="px-3 text-[11px] font-bold text-[#C5A059] uppercase tracking-wider mb-1">More</p>
              {DIRECT_LINKS.map(link => (
                <Link key={link.path} to={link.path} onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 text-sm font-medium text-gray-300 hover:text-[#C5A059] hover:bg-white/5 rounded-sm">
                  {link.name}
                </Link>
              ))}
            </div>
            {isAuthenticated && user ? (
              <>
                {user.role === 'admin' && (
                  <button
                    onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                    className="block w-full text-left px-3 py-2 text-sm font-medium text-[#C5A059] hover:bg-white/5 rounded-sm">
                    Admin Dashboard
                  </button>
                )}
                <button onClick={logout}
                  className="block w-full text-left px-3 py-2 text-sm font-medium text-red-400 hover:bg-white/5 rounded-sm">
                  Logout
                </button>
              </>
            ) : (
              <Button
                onClick={() => { login(); setMobileMenuOpen(false); }}
                className="w-full bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm px-6 py-2 font-semibold uppercase text-sm">
                Join Community
              </Button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
